import logger from '@/lib/logger';
import { getSiteUrl } from '@/lib/auth/site-url';
import {
  ACTIVE_AGENCY_COOKIE,
  ACTIVE_DEPARTMENT_COOKIE,
} from '@/lib/access-control/activeScope';
import { invalidateAllDedupedFetches } from '@/lib/utils/dedupedFetch';

/**
 * Client-side logout utilities
 *
 * Handles clearing localStorage data that persists across sessions.
 * This is CRITICAL - without this, stale role data causes the device
 * setup screen to show the wrong configuration after logout/login.
 */

/**
 * Keys that store user-specific data and MUST be cleared on logout.
 * These are used by CustomPreJoin to populate the device setup form.
 */
const AUTH_LOCALSTORAGE_KEYS = [
  'lk-user-role',
  'lk-user-firstName',
  'lk-user-lastName',
  'lk-user-badgeNumber',
  'lk-user-caseNumber',
  'lk-stt-language',
  'neutralaiz-navigation-cache',
] as const;

/**
 * Clears all auth-related localStorage keys and session cookies.
 *
 * MUST be called before logout to prevent stale data from
 * polluting the next session.
 */
export function clearAuthLocalStorage(): void {
  if (typeof window === 'undefined') return;

  AUTH_LOCALSTORAGE_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      logger.warn('logout', 'Failed to remove localStorage key ""', { key: key, e });
    }
  });

  try {
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (key?.startsWith('neutralaiz-navigation-cache:')) {
        localStorage.removeItem(key);
      }
    }
  } catch (e) {
    logger.warn('logout', 'Failed to clear navigation cache keys', { e });
  }

  // Best-effort clear for legacy non-HttpOnly masquerade cookies.
  // The active masquerade cookie is cleared server-side during sign-out.
  try {
    document.cookie = 'neutralaiz-masquerade-roles=; path=/; max-age=0; SameSite=Lax';
  } catch (e) {
    logger.warn('logout', 'Failed to clear masquerade cookie', { e });
  }

  try {
    document.cookie = `${ACTIVE_AGENCY_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
    document.cookie = `${ACTIVE_DEPARTMENT_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
  } catch (e) {
    logger.warn('logout', 'Failed to clear active org scope cookies', { e });
  }

  // Wipe in-memory dedup cache so next user never sees previous user's data
  // (cache is keyed by URL only — masquerade roles, simulator state, etc.)
  invalidateAllDedupedFetches();

  logger.debug('logout', 'Cleared auth localStorage keys and auth-scoped cookies');
}

/**
 * Performs a full client-side logout:
 * 1. Clears localStorage
 * 2. Redirects to signout route
 *
 * Use this instead of directly navigating to /auth/signout
 */
export function performLogout(redirectTo?: string): void {
  clearAuthLocalStorage();

  // Build signout URL with optional redirect
  // Use centralized site URL utility for security (env var with fallback)
  const signoutUrl = new URL('/auth/signout', getSiteUrl());
  if (redirectTo) {
    signoutUrl.searchParams.set('redirect', redirectTo);
  }

  // Navigate to server-side signout
  window.location.href = signoutUrl.toString();
}
